<template>
  <!--
    Static "As seen in" press-logo strip.

    Replaces the old animated CarouselComponent — the user feedback was that
    the infinite-scroll on mobile felt fidgety and pulled the eye away from
    the stats above. A static row with the four press logos (linked to the
    original articles for SEO/trust) works better and removes the JS timer.

    Each logo wraps its <img> in an <a> so crawlers get the article context.
    A visually-hidden .sr-only span inside each <a> gives Google rich anchor
    text (the aria-label alone is not used as anchor text by the crawler).
  -->
  <ul class="press-strip" aria-label="Press coverage of GYMPODS">
    <li v-for="logo in logos" :key="logo.id" class="press-item">
      <a
        :href="logo.href"
        target="_blank"
        rel="noopener noreferrer"
        :aria-label="`Read ${logo.alt}: ${logo.description} (opens in new tab)`"
        class="press-link"
      >
        <picture>
          <source type="image/webp" :srcset="logo.webp" />
          <img
            loading="lazy"
            decoding="async"
            :src="logo.src"
            :alt="logo.alt"
            :width="logo.width"
            :height="logo.height"
            class="press-logo"
          />
        </picture>
        <span class="sr-only">Read {{ logo.alt }}: {{ logo.description }}</span>
      </a>
    </li>
  </ul>
</template>

<script>
export default {
  name: 'PressLogoStrip',
  data() {
    return {
      // width/height match the natural source dimensions and exist
      // to give the browser an aspect ratio at parse-time, eliminating
      // the layout shift Lighthouse was flagging on this strip. The
      // CSS still controls display size (.press-logo: width: 100px on
      // mobile, 140px on desktop, height: auto).
      logos: [
        {
          id: 1,
          src: '/assets/womens-fitness.png',
          webp: '/assets/womens-fitness.webp',
          width: 900,
          height: 300,
          alt: "Women's Fitness",
          href: 'https://womensfitness.co.uk/motivation/fitness-trends/',
          description: 'fitness trends feature on private gym PODs',
        },
        {
          id: 2,
          src: '/assets/womens-health.png',
          webp: '/assets/womens-health.webp',
          width: 600,
          height: 338,
          alt: "Women's Health",
          href: 'https://www.womenshealthmag.com/uk/collective/healthy-habits/a44597531/are-private-exercise-pods-the-key-to-ending-gym-timidation/',
          description: 'private exercise PODs and ending gym-timidation',
        },
        {
          id: 3,
          src: '/assets/mens-fitness.png',
          webp: '/assets/mens-fitness.webp',
          width: 654,
          height: 381,
          alt: "Men's Fitness",
          href: 'https://mensfitness.co.uk/features/gymtimidation/',
          description: 'gymtimidation and the rise of private gym PODs',
        },
        {
          id: 4,
          src: '/assets/mail-online.png',
          webp: '/assets/mail-online.webp',
          width: 301,
          height: 167,
          alt: 'Mail Online',
          href: 'https://www.dailymail.com/news/article-13287257/rise-gym-video-fitness-influencers-streaming-workouts.html',
          description: 'the rise of private gym PODs and streaming workouts',
        },
      ],
    };
  },
};
</script>

<style scoped>
/* Visually-hidden text for Google crawler & screen readers. */
.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

.press-strip {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: 24px 40px;
  width: 100%;
}

.press-item {
  display: flex;
  align-items: center;
  justify-content: center;
}

.press-link {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  transition: transform 0.25s ease, opacity 0.25s ease;
  outline: none;
}

.press-link:hover,
.press-link:focus-visible {
  transform: translateY(-2px);
  opacity: 0.85;
}

.press-link:focus-visible {
  outline: 2px solid #7fc0c3;
  outline-offset: 4px;
}

.press-logo {
  width: 100px;
  height: auto;
  display: block;
}

@media (min-width: 925px) {
  .press-strip {
    gap: 32px 56px;
  }

  .press-logo {
    width: 140px;
  }
}
</style>
